import { call, put } from 'redux-saga/effects';

import * as types from '../constants/ActionTypes';
import { LIMITED_LOGIN_SEND_MESSAGE_TIMER_LENGTH } from '../constants/AppConstants';
import AttachmentsApi from '../apis/attachments';
import MessagesApi from '../apis/messages';
import {
    getAttachmentError,
    getAttachmentSuccess,
} from '../actions/attachments/attachmentsActions';
import {
    conversationError,
    getMessagesSuccess,
    limitedLoginSendMessageError, limitedLoginSendMessageSuccess,
    replyError,
} from '../actions/messages/messagesActions';
import { apiRequestStart, apiRequestEnd } from '../actions/loadingState/loadingStateActions';
import { startLimitedLoginMessageTimer, stopLimitedLoginMessageTimer} from '../actions/session/sessionActions';

export function* getAttachmentSaga(action) {
    const { id, mimeType, type } = action;

    try {
        yield put(apiRequestStart(type));

        const response = yield call(AttachmentsApi.getAttachment, id);

        // We do not pass back a successful flag in this response
        if (response && response.data) {
            const blob = new Blob([response.data], {type: mimeType});

            // Some browsers need webkit prefix, but most work with window.URL
            const url = (window.URL || window.webkitURL || window || {}).createObjectURL(blob);
            const data = {id, url};
            yield put(getAttachmentSuccess(data));
        }
        else {
            // error message should display per Attachment
            yield put(getAttachmentError(id, response));
        }
    }
    catch (error) {
        console.error('Error calling getAttachment! ', error);
        yield put(getAttachmentError(id, error));
    }
    finally {
        yield put(apiRequestEnd(type));
    }
}

export function* getMessagesSaga(action) {
    const { id, type } = action;

    try {
        if (id === 0) {
            // If this is the initial load, start LoadingState
            yield put(apiRequestStart(type));
        }

        const response = yield call(MessagesApi.getMessages, id);

        // Check all the important objects, if any are missing, throw an error
        if (response && response.data && response.data.success) {

            // Sort the messages in ascending order
            response.data.messagesList.sort((a, b) => (a.dateTimeSent > b.dateTimeSent) ? 1 : -1);

            const { messagesList } = response.data;
            response.data.latestMessageId = getLastMessageId(messagesList, id);
            yield put(getMessagesSuccess(response.data));
        }
        // If for some reason the catalyst servlet is in an error state, we need to return the error
        else {
            yield put(conversationError(response));
        }
    }
    catch (error) {
        console.error('Error calling getMessages!', error);
        yield put(conversationError(error.response));
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(type));
    }
}

// This will return undefined on an empty list
const getLastMessageId = (arr, defaultId) => {
    const last = arr && Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : '';

    if (last && last !== '') {
        const { id } = last;
        return id;
    }
    return defaultId;
};

export function* limitedLoginSendMessageSaga(action) {
    // Restart same timer if for some reason the message failed to send.
    const reduxTimerPayload = {
        actionName: types.LIMITED_LOGIN_SEND_MESSAGE_TIMER,
        timerName: types.LIMITED_LOGIN_SEND_MESSAGE_TIMER,
        timerPeriod: LIMITED_LOGIN_SEND_MESSAGE_TIMER_LENGTH,
    };

    try {
        // start LoadingState
        yield put(apiRequestStart(action.type));

        const { formData } = action;
        const replyResponse = yield call(MessagesApi.replyToConversation, formData);

        yield put (stopLimitedLoginMessageTimer({ timerName: types.LIMITED_LOGIN_SEND_MESSAGE_TIMER }));

        // Check all the important objects, if any are missing, throw an error
        if (replyResponse && replyResponse.data && replyResponse.data.success) {
            yield put(limitedLoginSendMessageSuccess(replyResponse.data));
        }
        // If for some reason the catalyst servlet is in an error state, we need to return the error
        else {
            yield put(limitedLoginSendMessageError(replyResponse));

            yield put (startLimitedLoginMessageTimer(reduxTimerPayload));
        }
    }
    catch (error) {
        console.error('Error sending message!', error);
        yield put(limitedLoginSendMessageError(error.response));
        yield put (startLimitedLoginMessageTimer(reduxTimerPayload));
    }
    finally {
        // We completed successfully, stop the timer and loading state ui.
        yield put(apiRequestEnd(action.type));
        yield put (stopLimitedLoginMessageTimer({ timerName: types.LIMITED_LOGIN_SEND_MESSAGE_TIMER }));
    }
}

export function* replyToConversationSaga(action) {
    try {
        // start LoadingState
        yield put(apiRequestStart(action.type));

        const { formData } = action;
        const replyResponse = yield call(MessagesApi.replyToConversation, formData);

        /**
         * Chain the call to getMessages so that the conversation is up to date.
         */
        const getMessagesResponse = yield call(MessagesApi.getMessages, 0);
        getMessagesResponse.data.latestMessageId = getLastMessageId(getMessagesResponse.data.messagesList, 0);

        // Check all the important objects, if any are missing, throw an error
        if (replyResponse && replyResponse.data && replyResponse.data.success) {
            yield put({ type: types.REPLY_TO_CONVERSATION_SUCCESS });
            yield put(getMessagesSuccess(getMessagesResponse.data));
        }
        // If for some reason the catalyst servlet is in an error state, we need to return the error
        else {
            yield put(replyError(replyResponse));
        }
    }
    catch (error) {
        console.error('Error replying to conversation!', error);
        yield put(replyError(error.response));
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(action.type));
    }
}